import {
    forEach as _forEach,
    isEmpty as _isEmpty,
    map as _map,
    partition as _partition,
    xorBy as _xorBy,
} from '@lodash';
import { ActionContext, Commit, Dispatch } from 'vuex';
import StoreUtil from '@store/StoreUtil';
import BookieDate from '@utils/date/BookieDate';
import OfferRequest from '@core/services/offer/_private/models/OfferRequest';
import StructService from '@core/services/offer/StructService';
import config from '@config';
import { ISportOfferState } from './index';
import * as types from './mutationTypes';
import OfferService from '@core/services/offer/OfferService';
import { broadcastService } from '@core/services/broadcast/BroadcastService';
import { errorGuard } from '@app/utils';
import Match from '@core/models/offer/Match';
import StructManager from '@core/services/common/StructManager';
import { ISuperOfferCollections, OddDynamics } from './types';
import { OddValueChange } from '@store/modules/data/sportOffer/enums';
import Odd from '@models/offer/Odd';
import { differenceInSeconds } from 'date-fns';
import { twoDecimals } from '@shared/filters';
import MarketGroupInfo from '@models/struct/MarketGroupInfo';
import Category from '@models/struct/Category';
import Tournament from '@models/struct/Tournament';
import Sport from '@models/struct/Sport';
import OddType from '@models/struct/OddType';
import { IState } from '@app/store';
import { getTimeSpanForTimeFilter } from '@shared/timeFilter/helpers';
import { TimeFilter } from '@shared/timeFilter/enums';
import OfferHostProvider from '@core/services/offer/OfferHostProvider';
import * as Sentry from '@sentry/vue';
import { Subscriptions } from './Subscriptions';
import { Market } from '@models/struct/Market';

const pendingEvents: Record<string, Match> = {};

// all changes socket has only final market and we must subscribe to super events
const subscribed: Record<number, boolean> = {};

const sseHandler = (
    event: Match | null,
    resourceId: string,
    timestamp: number,
    localEvent: Readonly<Match>,
    addHandler: (event: Match) => void,
    fetchHandler: () => void,
    updateHandler: (event: Match) => void,
    errorMessage: string
) => {
    // we received event data but event is not in our local store we must add it
    // else if event isn't in our store and there was no event data, fetch the offer manually
    if (!localEvent && !!event) {
        addHandler(event);

        return;
        // tslint:disable-next-line:no-else-after-return
    } else if (!localEvent && !event) {
        fetchHandler();

        return;
    }

    // Local data is newer or same as the one in sse message
    if (timestamp <= localEvent.matchTimestamp!) {
        return;
    }

    // Local data is older, but we don't have event data available, we must fetch it manually
    if (timestamp > localEvent.matchTimestamp! && !event) {
        // @ts-ignore
        fetchHandler();

        return;
    }

    // Local data is older, and we need to update with the event data in the sse message
    if (!!event && timestamp > localEvent.matchTimestamp!) {
        updateHandler(event);
    } else {
        Sentry.captureMessage(errorMessage, {
            extra: {
                localTimestamp: localEvent.matchTimestamp,
                sseData: {
                    resourceId,
                    timestamp
                }
            }
        });
    }
};

export default {
    updateMarketGroupInfos: StoreUtil.asyncErrorGuard((
        { commit, dispatch, state }: { commit: any; dispatch: Dispatch; state: ISportOfferState },
        marketGroupInfo: MarketGroupInfo) => {
        commit(types.SET_MARKET_GROUP_INFOS, {
            ...state.marketGroupInfos,
            [marketGroupInfo.id]: marketGroupInfo,
        });
        StructManager.getInstance().setMarketGroupInfos(state.marketGroupInfos);
        dispatch('denormalizePendingEvents');
    }),

    fetchStruct: StoreUtil.asyncErrorGuard(
        async (
            { commit, dispatch, getters }: { commit: Commit, dispatch: Dispatch, getters: any },
        ) => {
            const struct = await StructService.getInstance().getStruct();

            let isUpdate = false;

            try {
                isUpdate = StructManager.getInstance().hasStruct();
            } catch (e) {
            }

            if (isUpdate) {
                StructManager.getInstance().setStruct(struct);
            } else {
                StructManager.createInstance({ struct });
            }

            commit(types.SET_STRUCT, struct);
            return struct;
        }),

    startOffer: StoreUtil.asyncErrorGuard(async ({ dispatch }: { dispatch: Dispatch }) => {
        try {
            dispatch('setIsOfferStarting', true);

            await dispatch('fetchStruct');
            OfferService.getInstance().startStreaming();

            // dispatch('fetchBroadcasts');
            dispatch('setIsOfferReady', true);
            dispatch('setIsOfferStarting', false);
            if (!OfferService.getInstance().sseEnabled()) {
                dispatch('startLiveEventsCounting');
            } else {
                dispatch('subscribeToLiveEventChangesSSE');
            }
            dispatch('data/competitions/prepareCompetitions', null, { root: true });
        } catch (err) {
            const timeout = OfferHostProvider.getInstance().retryTimeout();
            console.log('SSBT::Start Offer Error! Restarting in:', timeout / 1000, 'sec >>', err);
            // RetailService.getInstance().requestGrafanaLog(err, 'OFFER');
            // RetailService.getInstance().requestPrometheusIncrement({ failure: 'OFFER' });
            setTimeout(() => window.location.reload(), timeout);
        }
    }),

    subscribeToStructChanges: StoreUtil.asyncErrorGuard(({ dispatch }: { dispatch: Dispatch }) => {
        const offerService = OfferService.getInstance();
        if (!offerService.sseEnabled()) {
            offerService.subscribeToMarketGroupInfoEvent(
                (marketGroupInfo: MarketGroupInfo) => {
                    dispatch('updateMarketGroupInfos', marketGroupInfo);
                }
            );
            offerService.subscribeToSportEvent(
                (sport: Sport) => dispatch('updateSports', sport),
            );

            offerService.subscribeToTournamentEvent(
                (tournament: Tournament) => dispatch('updateTournaments', tournament),
            );

            offerService.subscribeToCategoryEvent(
                (category: Category) => dispatch('updateCategories', category),
            );

            offerService.subscribeToOddTypeEvent(
                (oddType: OddType) => dispatch('updateOddTypes', oddType),
            );

            offerService.subscribeToMarketEvent(
                (market: Market) => dispatch('updateMarkets', market),
            );
        }
    }),

    fetchEvents: StoreUtil.asyncErrorGuard(async (
        { getters, dispatch, commit }: { getters: any; dispatch: Dispatch; commit: Commit },
        timeSpan?: { startDate: Date; endDate: Date }
    ) => {
        const { startDate, endDate } = getStartAndEndDate(timeSpan);
        const [prematchEvents, outrights, live] = await Promise.all([
            OfferService.getInstance().getEvents(startDate, endDate),
            OfferService.getInstance().getOutrights(),
            OfferService.getInstance().getLiveEvents(endDate)
        ]);

        const combinedPrematch = [...prematchEvents, ...outrights];

        const prematchEventsToAdd: Match[] = [];

        combinedPrematch.forEach((event) => {
            if (!getters.eventsMap[event.id]) {
                prematchEventsToAdd.push(event);
            } else if (event.incrementId >= getters.eventsMap[event.id].incrementId) {
                commit(types.UPDATE_EVENT, event);
            }
        });

        const isLiveEventStale = (event: Match) => !getters.eventsMap[event.id]
            || getters.eventsMap[event.id].matchTimestamp! < event.matchTimestamp!;

        dispatch(
            'setEvents',
            {
                events: [
                    ...prematchEventsToAdd,
                    ...live.filter(isLiveEventStale),
                ],
            }
        );
        commit(types.SET_IS_OFFER_FETCHED, true);
    }),
    fetchOutrights: StoreUtil.asyncErrorGuard(async (
        { getters, dispatch, commit }: { getters: any; dispatch: Dispatch; commit: Commit }
    ) => {
        const outrights = await OfferService.getInstance().getOutrights();

        const outrightsEventsToAdd: Match[] = [];

        outrights.forEach((event) => {
            if (!getters.eventsMap[event.id]) {
                outrightsEventsToAdd.push(event);
            } else if (event.incrementId >= getters.eventsMap[event.id].incrementId) {
                commit(types.UPDATE_EVENT, event);
            }
        });

        dispatch(
            'setEvents',
            {
                events: [
                    ...outrightsEventsToAdd
                ],
            }
        );
    }),
    updateLiveEvents: StoreUtil.asyncErrorGuard(async (
        { getters, dispatch }: { getters: any; dispatch: Dispatch },
    ) => {
        const fetchedEvents = await OfferService.getInstance().getLiveEvents(new Date());
        const storedEvents = getters.events.filter((event: Match) => event.isOngoing());
        const eventIds = _xorBy(fetchedEvents, storedEvents, 'id').map((event) => event.id);
        dispatch('fetchEventsByIds', eventIds);
    }),

    fetchTournament: StoreUtil.asyncErrorGuard(async (
        { getters, dispatch }: { getters: any; dispatch: Dispatch },
        { tournamentId, timeSpan, preselected = true }:
            { tournamentId: number; timeSpan: { startDate: Date; endDate: Date }; preselected: boolean }
    ) => {
        const { startDate, endDate } = getStartAndEndDate(timeSpan);
        const [preEvent, live] = await Promise.all([
            OfferService.getInstance().getEventsByTournament(
                tournamentId,
                startDate,
                endDate,
                new OfferRequest({ preselected })
            ),
            OfferService.getInstance().getLiveEvents(endDate)
        ]);

        dispatch(
            'setEvents',
            {
                events: [
                    ...preEvent.filter((event) => !getters.eventsMap[event.id]
                        || getters.eventsMap[event.id].incrementId !== event.incrementId),
                    ...live.filter((event) => !getters.eventsMap[event.id]
                        || getters.eventsMap[event.id].incrementId !== event.incrementId),
                ],
            }
        );
    }),

    fetchAll: StoreUtil.asyncErrorGuard(async (
        { dispatch }: { dispatch: Dispatch }
    ) => {
        const timeSpan = getTimeSpanForTimeFilter(TimeFilter.all);
        await dispatch('fetchEvents', timeSpan);
    }),

    fetchTopTenEvents: StoreUtil.asyncErrorGuard(async (
        { commit, dispatch }: { commit: Commit, dispatch: Dispatch }) => {
        const topTenEvents = await OfferService.getInstance().getTopTenEvents();
        commit(types.SET_TOP_TEN_EVENTS, _map(topTenEvents, 'id'));
        dispatch(
            'setEvents',
            {
                events: topTenEvents,
            }
        );
        commit(types.SET_ARE_TOP_TEN_EVENTS_FETCHED, true);
    }),

    fetchSuperEvents: StoreUtil.asyncErrorGuard(
        async (context: ActionContext<ISportOfferState, IState>) => {
            if (context.getters['isSuperOfferFetched']) {
                return;
            }

            const superOffer = await OfferService.getInstance().getSuperEvents(
                BookieDate.getStartDateForDate(),
                BookieDate.getEndDateForDate()
            );

            const availableSuperOffers = context.state.superOfferConfig;
            const promotionEvents = {} as ISuperOfferCollections;
            const events = [] as Match[];
            _forEach(superOffer, (promoEvents, key) => {
                const realKey = key;

                const promotionData = availableSuperOffers[realKey];
                if (promotionData && promoEvents.length) {
                    const promoMarketOdd = promoEvents[0].getOdds().find(odd =>
                        (!!odd.tags && odd.tags.includes(realKey)));
                    if (!promoMarketOdd) {
                        delete promotionEvents[key];
                        return;
                    }
                    promotionEvents[key] = {
                        matchIds: promoEvents.map(m => m.id),
                        primaryMarketId: promoMarketOdd!.marketId,
                    };
                    events.push(...promoEvents);
                }
            });

            context.dispatch(
                'setEvents',
                {
                    events,
                }
            );
            context.commit(types.SET_PROMOTIONAL_EVENTS, promotionEvents);
            context.commit(types.SET_IS_SUPER_OFFER_FETCHED, true);

            _forEach(promotionEvents, (group) => {
                group.matchIds.forEach((id) => {
                    if (!subscribed[id]) {
                        context.dispatch('subscribeToEvent', id);
                        subscribed[id] = true;
                    }
                });
            });
        }),

    subscribeToEventChanges({ dispatch, state }: { dispatch: Dispatch, state: ISportOfferState }) {
        if (OfferService.getInstance().sseEnabled()) {
            OfferService.getInstance().subscribeToAllEventChangesSSE(
                ({ event, resourceId, timestamp }) => {
                    // Check if we have the event in our local state and that we don't have an active subscription
                    // If we have the subscription, it will handle updating the event
                    if (!Subscriptions.hasSubscription(Number(resourceId))) {
                        const addHandler = (event: Match) => dispatch('setEvents', { events: [event] });
                        const fetchHandler = () => dispatch('fetchEvents');
                        const updateHandler = (event: Match) => {
                            dispatch('_purgeOddValueDynamics');
                            dispatch('_setOddValueDynamics', [event]);
                            dispatch('setEvent', event);
                        };
                        const errorMessage =
                            'Something unknown happened while updating all events from sse message';

                        sseHandler(
                            event,
                            resourceId,
                            timestamp,
                            state.events[resourceId],
                            addHandler,
                            fetchHandler,
                            updateHandler,
                            errorMessage
                        );
                    }
                }
            );
        } else {
            OfferService.getInstance().subscribeToAllEventChanges((events) => {
                errorGuard({
                    action: async () => {
                        dispatch('_purgeOddValueDynamics');

                        if (_isEmpty(events)) {
                            return;
                        }

                        dispatch('_setOddValueDynamics', events);
                        dispatch(
                            'setEvents',
                            {
                                events,
                            }
                        );
                    },
                });
            });
        }
    },
    subscribeToLiveEventChangesSSE({ dispatch, state }: { dispatch: Dispatch, state: ISportOfferState }) {
        if (OfferService.getInstance().sseEnabled()) {
            OfferService.getInstance().subscribeToLiveEventChangesSSE(
                (events) => events.map(({ event, resourceId, timestamp }) => {
                    const addHandler = (event: Match) => dispatch('setEvents', { events: [event] });
                    const fetchHandler = () => dispatch('fetchEvents');
                    const updateHandler = (event: Match) => {
                        dispatch('_purgeOddValueDynamics');
                        dispatch('_setOddValueDynamics', [event]);
                        dispatch('setEvent', event);
                    };
                    const errorMessage =
                        'Something unknown happened while updating all events from sse message';

                    sseHandler(
                        event,
                        resourceId,
                        timestamp,
                        state.events[resourceId],
                        addHandler,
                        fetchHandler,
                        updateHandler,
                        errorMessage
                    );
                }),
            );
        }
    },
    subscribeToAllOutrightEventChanges ({ dispatch, state }: { dispatch: Dispatch, state: ISportOfferState }) {
        if (OfferService.getInstance().sseEnabled()) {
            console.log('OUTRIGHTS SUBSCRIBE');
            OfferService.getInstance().subscribeToAllOutrightEventChanges(
                ({ event, resourceId, timestamp }) => {
                    // Check if we have the event in our local state and that we don't have an active subscription to it
                    // If we have the subscription, it will handle updating the event
                    if (!Subscriptions.hasSubscription(Number(resourceId))) {
                        const addHandler = (event: Match) => dispatch('setEvents', { events: [event] });
                        const fetchHandler = () => dispatch('fetchEvents');
                        const updateHandler = (event: Match) => {
                            dispatch('_purgeOddValueDynamics');
                            dispatch('_setOddValueDynamics', [event]);
                            dispatch('setEvent', event);
                        };
                        const errorMessage =
                            'Something unknown happened while updating all outrights from sse ';

                        sseHandler(
                            event,
                            resourceId,
                            timestamp,
                            state.events[resourceId],
                            addHandler,
                            fetchHandler,
                            updateHandler,
                            errorMessage
                        );
                    }
                }
            );
        }
    },

    setEvents(
        { commit, dispatch }: { commit: Commit, dispatch: Dispatch },
        { events }: { events: Match[] }
    ) {
        const [valid, invalid] = _partition(events, (e) => e.validStruct);

        // valid.forEach((event) => {
        // logSocketRawEvent('setEvents', event);
        // });

        commit(types.SET_EVENTS, valid);
        commit(types.INCREMENT_ALL_CHANGES_ID);
        if (invalid.length > 0) {
            dispatch('retryInvalidEvents', invalid);
        }
    },

    retryInvalidEvents: StoreUtil.asyncErrorGuard(async (
        { dispatch }: { dispatch: Dispatch },
        pending: Match[]
    ) => {
        pending.forEach(event => pendingEvents[event.id] = event);
        await dispatch('fetchStruct');
        dispatch('denormalizePendingEvents');
    }),

    denormalizePendingEvents({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
        Object.keys(pendingEvents).forEach(id => {
            const event = pendingEvents[id];
            OfferService.getInstance().denormalizeEvent(event);
            if (event.validStruct) {
                delete pendingEvents[id];
                dispatch('_setOddValueDynamics', [event]);
                commit(types.SET_EVENT, event);
            }
        });
    },

    removeSubscription({ commit, state }: { commit: Commit; state: ISportOfferState }, eventId: number) {
        if (!!subscribed[eventId]) return;
        Subscriptions.removeSubscription(eventId);
        state.subscriptions[eventId].unsubscribe();
        commit(types.REMOVE_EVENT_SUBSCRIPTION, eventId);
    },

    getEvent: StoreUtil.asyncErrorGuard(async (
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        eventId: number
    ) => {
        // because pick is a new component and on expanding and it shows
        // odd changes because oddValueDynamics still contains the last value for a short period of time
        dispatch('_deleteFromOddValueDynamics', eventId);
        dispatch('subscribeToEvent', eventId);

        const event = await OfferService.getInstance().getEvent(eventId);
        if (!event) {
            return;
        }
        commit(types.SET_EVENT, event);
    }),

    subscribeToEvent: StoreUtil.asyncErrorGuard(async (
        { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch, state: ISportOfferState },
        eventId: number
    ) => {
        if (!Subscriptions.hasSubscription(eventId)) {
            const subscription = OfferService.getInstance().sseEnabled() ?
                OfferService.getInstance().subscribeToEventSSE(
                    eventId,
                    async ({ event, resourceId, timestamp }) => {
                        const fetchHandler = async () => {
                            const newEvent = await OfferService.getInstance().getEventsById([Number(resourceId)]);
                            dispatch('setEvents', [newEvent]);
                        };
                        const updateHandler = (event: Match) => {
                            dispatch('_purgeOddValueDynamics');
                            dispatch('_setOddValueDynamics', [event]);
                            dispatch('setEvent', event);
                        };
                        const errorMessage =
                            'Something unknown happened while updating single event from sse message';
                        sseHandler(
                            event,
                            resourceId,
                            timestamp,
                            state.events[resourceId],
                            () => {
                            }, // we don't need this here
                            fetchHandler,
                            updateHandler,
                            errorMessage
                        );
                    }
                )
                : OfferService.getInstance().subscribeToEvent(eventId, (event) => {
                    dispatch('_setOddValueDynamics', [event]);
                    commit(types.UPDATE_EVENT, event);
                });
            Subscriptions.addSubscription(eventId, subscription);
            commit(types.SET_EVENT_SUBSCRIPTION, { eventId, subscription });
        }
    }),

    fetchEventsByIds: StoreUtil.asyncErrorGuard(async (
        { getters, dispatch }: { getters: any; dispatch: Dispatch }, eventIds: number[]
    ) => {
        const events = await OfferService.getInstance().getEventsById(eventIds);
        dispatch(
            'setEvents',
            {
                events: events.filter((event) => !getters.eventsMap[event.id]
                    || getters.eventsMap[event.id].incrementId !== event.incrementId),
            }
        );
    }),

    setIsOfferStarting: StoreUtil.createSimpleMutatorAction(types.SET_IS_OFFER_STARTING),
    setIsOfferReady: StoreUtil.createSimpleMutatorAction(types.SET_IS_OFFER_READY),
    setShouldFetchOffer: StoreUtil.createSimpleMutatorAction(types.SET_SHOULD_FETCH_OFFER),
    fetchBroadcasts: StoreUtil.asyncErrorGuard(async ({ commit }: { commit: any }) => {
        const broadcasts = await broadcastService.getBroadcasts();
        commit(types.SET_BROADCASTS, broadcasts);
    }),
    setEvent: StoreUtil.createSimpleMutatorAction(types.SET_EVENT),
    setAreSubscriptionsSet: StoreUtil.createSimpleMutatorAction(types.SET_ARE_SUBSCRIPTIONS_SET),

    fetchAllOffer({ dispatch, getters }: { dispatch: Dispatch; getters: any }) {
        if (getters.isOfferReady) {
            dispatch('fetchAll');
            dispatch('fetchTopTenEvents');
            dispatch('fetchSuperEvents');
        }
    },
    startLiveEventsCounting: async ({ commit }: { commit: Commit }) => {
        OfferService.getInstance().subscribeToLiveEventsCountChangesStream(
            (eventsCount: number) => {
                errorGuard({
                    action: () => commit(types.SET_LIVE_EVENTS_COUNT, eventsCount)
                });
            }
        );
    },

    _purgeOddValueDynamics(
        { getters, commit }: { getters: any; commit: Commit },
    ) {
        const oddChanges: Record<number, Record<string, OddDynamics>> = {};

        const now = new Date();
        _forEach(getters.oddValueDynamics, (dynamicsItems: Record<string, OddDynamics>, eventId: string) => {
            const eventIdInt = parseInt(eventId, 10);
            _forEach(dynamicsItems, (oddDynamics: OddDynamics, oddId: string) => {
                if (differenceInSeconds(now, oddDynamics.created) < config.app.sportOffer.oddDynamicsPreserveTime) {
                    if (!oddChanges[eventIdInt]) {
                        oddChanges[eventIdInt] = {};
                    }
                    oddChanges[eventIdInt][oddId] = oddDynamics;
                }
            });
        });

        commit(types.SET_ODD_VALUE_DYNAMICS, oddChanges);
    },
    setExpandedMarketIds({ commit }: { commit: Commit }, markets: any) {
        commit(types.SET_EXPANDED_MARKET_IDS, markets);
    },
    toggleExpandedMarketId({ commit }: { commit: Commit }, marketId: number) {
        commit(types.TOGGLE_EXPANDED_MARKET_ID, { key: marketId });
    },
    _deleteFromOddValueDynamics(
        { getters, commit }: { getters: any; commit: Commit },
        eventId: number
    ) {
        const oddChanges = { ...getters.oddValueDynamics };
        delete oddChanges[eventId];
        commit(types.SET_ODD_VALUE_DYNAMICS, oddChanges);
    },
    clearSubscriptions({ dispatch }: { dispatch: Dispatch; }) {
        Subscriptions.clearSubscriptions();
    },

    _setOddValueDynamics(
        { getters, commit }: { getters: any; commit: Commit },
        events: Match[]
    ) {
        const oddChanges = { ...getters.oddValueDynamics };
        const now = new Date();
        events.forEach((event) => {
            if (getters.eventsMap[event.id]) {
                const oldOdds = getters.eventsMap[event.id].getOdds().reduce(
                    (a: Record<string, number>, odd: Odd) => {
                        a[odd.uniqueId] = odd.value;
                        return a;
                    },
                    {}
                );

                if (!oddChanges[event.id]) {
                    oddChanges[event.id] = {};
                }

                event.getOdds().forEach((odd) => {
                    const oldOddValue = oldOdds[odd.uniqueId];
                    if (oldOddValue && twoDecimals(oldOddValue) !== twoDecimals(odd.value)) {
                        let newDirection = odd.value < oldOddValue ? OddValueChange.down1 : OddValueChange.up1;
                        const oldChange = oddChanges[event.id][odd.uniqueId];

                        if (oldChange) {
                            if (newDirection < 0 && oldChange.oddDirection < 0) {
                                newDirection = (oldChange.oddDirection) % 2 - 1;
                            } else if (newDirection > 0 && oldChange.oddDirection > 0) {
                                newDirection = (oldChange.oddDirection) % 2 + 1;
                            }
                        }

                        oddChanges[event.id][odd.uniqueId] = {
                            oddDirection: newDirection,
                            created: now,
                        };
                    }
                });

                if (_isEmpty(oddChanges[event.id])) {
                    delete oddChanges[event.id];
                }
            }
        });

        commit(types.SET_ODD_VALUE_DYNAMICS, oddChanges);
    },

};

function getStartAndEndDate(timeSpan?: { startDate: Date; endDate: Date }) {
    let startDate;
    let endDate;
    if (timeSpan) {
        ({ startDate, endDate } = timeSpan);
    } else {
        startDate = BookieDate.getStartDateForDate();
        endDate = BookieDate.getEndDateForDate();
    }

    return {
        startDate,
        endDate
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/app/store/modules/data/sportOffer/actions.ts